import { useState, useRef } from 'react';
import { useModalStore } from '../../../stores/modalStore.js';
import { useRoleStore } from '../../../stores/roleStore.js';
import { useServerStore } from '../../../stores/serverStore.js';
import { useFocusTrap } from '../../../hooks/useFocusTrap.js';

const STEPS = ['welcome', 'rules', 'interests', 'roles', 'done'];

const INTEREST_OPTIONS = [
  { id: 'gaming',    label: '🎮 Gaming' },
  { id: 'dev',       label: '💻 Development' },
  { id: 'music',     label: '🎵 Music' },
  { id: 'art',       label: '🎨 Art & Design' },
  { id: 'anime',     label: '🌸 Anime & Manga' },
  { id: 'sports',    label: '⚽ Sports' },
  { id: 'movies',    label: '🎬 Movies & TV' },
  { id: 'study',     label: '📚 Study & Education' },
  { id: 'crypto',    label: '₿ Crypto & Finance' },
  { id: 'travel',    label: '✈️ Travel' },
];

function StepDot({ total, current }) {
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginBottom: 24 }}>
      {Array.from({ length: total }).map((_, i) => (
        <div key={i} style={{ width: i === current ? 20 : 8, height: 8, borderRadius: 4, background: i === current ? '#6366f1' : i < current ? 'rgba(99,102,241,0.4)' : 'rgba(255,255,255,0.1)', transition: 'all 0.3s' }} />
      ))}
    </div>
  );
}

export function OnboardingModal() {
  const { isOpen, type, data, onClose } = useModalStore();
  const currentServerId = useServerStore(s => s.currentServerId);
  const { getServerRoles } = useRoleStore();

  const [step, setStep] = useState(0);
  const [interests, setInterests] = useState([]);
  const [pickedRoles, setPickedRoles] = useState([]);
  const [rulesAccepted, setRulesAccepted] = useState(false);

  const ref = useRef();
  useFocusTrap(ref, isOpen && type === 'onboarding');

  if (!isOpen || type !== 'onboarding') return null;

  const server   = data?.server || { name: 'this server', icon: '🌐', rules: 'Be respectful. No spam. No hate speech.' };
  const serverId = data?.serverId || currentServerId;
  const roles    = getServerRoles(serverId).filter(r => r.mentionable && !r.isEveryone);
  const totalSteps = roles.length > 0 ? 5 : 4;

  const STEP_KEYS = roles.length > 0 ? STEPS : STEPS.filter(s => s !== 'roles');

  const next = () => setStep(s => Math.min(s + 1, STEP_KEYS.length - 1));
  const back = () => setStep(s => Math.max(s - 1, 0));
  const currentKey = STEP_KEYS[step];

  const toggleInterest = (id) => setInterests(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  const toggleRole = (id) => setPickedRoles(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', zIndex: 1400, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div ref={ref} role="dialog" aria-modal="true" aria-label="Welcome"
        style={{ background: '#16213e', border: '1px solid rgba(255,255,255,0.08)', borderRadius: 16, width: 480, maxWidth: '95vw', padding: '32px', boxShadow: '0 40px 100px rgba(0,0,0,0.8)', overflow: 'hidden' }}>

        <StepDot total={STEP_KEYS.length} current={step} />

        {/* Welcome */}
        {currentKey === 'welcome' && (
          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <div style={{ fontSize: 52 }}>{server.icon || '🌐'}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>Welcome to {server.name}!</div>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, maxWidth: 340 }}>
              Before you dive in, let's get you set up. This will only take a minute.
            </div>
            <button onClick={next} style={{ marginTop: 8, padding: '11px 32px', borderRadius: 8, background: '#6366f1', border: 'none', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
              Get Started →
            </button>
          </div>
        )}

        {/* Rules */}
        {currentKey === 'rules' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ fontSize: 20, fontWeight: 700, color: '#fff' }}>📜 Server Rules</div>
            <div style={{ padding: '14px', borderRadius: 8, background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.07)', fontSize: 13, color: 'rgba(255,255,255,0.7)', lineHeight: 1.7, whiteSpace: 'pre-wrap', maxHeight: 200, overflowY: 'auto' }}>
              {server.rules || '1. Be respectful to all members.\n2. No spam or self-promotion.\n3. No hate speech or harassment.\n4. Follow the channel topic guidelines.\n5. Have fun!'}
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input type="checkbox" checked={rulesAccepted} onChange={e => setRulesAccepted(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: '#6366f1', cursor: 'pointer' }} />
              <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.65)' }}>I've read and agree to the server rules</span>
            </label>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button onClick={back} style={{ padding: '8px 18px', borderRadius: 7, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', fontSize: 13, cursor: 'pointer' }}>← Back</button>
              <button onClick={next} disabled={!rulesAccepted}
                style={{ padding: '8px 20px', borderRadius: 7, background: rulesAccepted ? '#6366f1' : 'rgba(99,102,241,0.3)', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: rulesAccepted ? 'pointer' : 'default' }}>
                I Agree →
              </button>
            </div>
          </div>
        )}

        {/* Interests */}
        {currentKey === 'interests' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#fff', marginBottom: 4 }}>🎯 Your Interests</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>Select anything that applies — helps us show relevant channels.</div>
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
              {INTEREST_OPTIONS.map(opt => {
                const on = interests.includes(opt.id);
                return (
                  <button key={opt.id} onClick={() => toggleInterest(opt.id)}
                    style={{ padding: '7px 14px', borderRadius: 20, fontSize: 13, fontWeight: 600, cursor: 'pointer', background: on ? 'rgba(99,102,241,0.25)' : 'rgba(255,255,255,0.05)', border: `1px solid ${on ? '#6366f1' : 'rgba(255,255,255,0.1)'}`, color: on ? '#a5b4fc' : 'rgba(255,255,255,0.55)', transition: 'all 0.15s' }}>
                    {opt.label}
                  </button>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button onClick={back} style={{ padding: '8px 18px', borderRadius: 7, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', fontSize: 13, cursor: 'pointer' }}>← Back</button>
              <button onClick={next} style={{ padding: '8px 20px', borderRadius: 7, background: '#6366f1', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {interests.length > 0 ? `Continue (${interests.length} selected)` : 'Skip →'}
              </button>
            </div>
          </div>
        )}

        {/* Self-assign roles */}
        {currentKey === 'roles' && roles.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <div style={{ fontSize: 20, fontWeight: 700, color: '#fff', marginBottom: 4 }}>🎭 Pick Your Roles</div>
              <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.45)' }}>These roles are open for members to self-assign.</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
              {roles.map(role => {
                const on = pickedRoles.includes(role.id);
                return (
                  <label key={role.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', borderRadius: 8, background: on ? 'rgba(99,102,241,0.1)' : 'rgba(255,255,255,0.04)', border: `1px solid ${on ? 'rgba(99,102,241,0.4)' : 'rgba(255,255,255,0.07)'}`, cursor: 'pointer', transition: 'all 0.15s' }}>
                    <span style={{ width: 12, height: 12, borderRadius: '50%', background: role.color || '#6b7280', flexShrink: 0 }} />
                    <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: on ? '#a5b4fc' : 'rgba(255,255,255,0.7)' }}>{role.name}</span>
                    <input type="checkbox" checked={on} onChange={() => toggleRole(role.id)} style={{ width: 15, height: 15, accentColor: '#6366f1', cursor: 'pointer' }} />
                  </label>
                );
              })}
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 4 }}>
              <button onClick={back} style={{ padding: '8px 18px', borderRadius: 7, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.6)', fontSize: 13, cursor: 'pointer' }}>← Back</button>
              <button onClick={next} style={{ padding: '8px 20px', borderRadius: 7, background: '#6366f1', border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>Continue →</button>
            </div>
          </div>
        )}

        {/* Done */}
        {currentKey === 'done' && (
          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14 }}>
            <div style={{ fontSize: 52 }}>🎉</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: '#fff' }}>You're all set!</div>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6, maxWidth: 320 }}>
              {pickedRoles.length > 0 && `${pickedRoles.length} role${pickedRoles.length > 1 ? 's' : ''} assigned. `}
              Welcome to {server.name}. Enjoy the community!
            </div>
            <button onClick={onClose}
              style={{ marginTop: 8, padding: '11px 32px', borderRadius: 8, background: '#6366f1', border: 'none', color: '#fff', fontSize: 15, fontWeight: 700, cursor: 'pointer' }}>
              Enter Server →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
